// src/components/TenantAdmin/ProgramTable.js
import React, { useMemo, useState } from 'react';
import { Button, Table, Badge, Modal, Form, Alert, Spinner, Card } from 'react-bootstrap';
import { createTenantProgram, updateTenantProgramStatus } from '../../api/api';

const ProgramTable = ({ programs = [], tenantSlug, onRefresh }) => {
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [error, setError] = useState('');

  const [form, setForm] = useState({ code: '', name: '', credits: '' });

  const sortedPrograms = useMemo(() => {
    const arr = Array.isArray(programs) ? [...programs] : [];
    arr.sort((a, b) => {
      const sa = (a.status || 'active').toLowerCase();
      const sb = (b.status || 'active').toLowerCase();
      if (sa !== sb) return sa === 'active' ? -1 : 1;
      return (a.code || '').localeCompare(b.code || '');
    });
    return arr;
  }, [programs]);

  const handleCreate = async () => {
    if (!form.code.trim() || !form.name.trim()) {
      setError('Code and Name are required.');
      return;
    }
    try {
      setCreating(true);
      setError('');
      await createTenantProgram(tenantSlug, {
        code: form.code.trim(),
        name: form.name.trim(),
        credits: form.credits ? parseInt(form.credits) : 0
      });
      setShowCreate(false);
      setForm({ code: '', name: '', credits: '' });
      onRefresh?.();
    } catch (e) { setError('Failed to create program.'); } 
    finally { setCreating(false); }
  };

  const toggleStatus = async (program) => {
    try {
      setUpdatingId(program.id);
      const next = (program.status || 'active').toLowerCase() === 'active' ? 'inactive' : 'active';
      await updateTenantProgramStatus(tenantSlug, program.id, next);
      onRefresh?.();
    } catch (e) { console.error(e); } 
    finally { setUpdatingId(null); }
  };

  return (
    <Card className="border-0 shadow-sm">
      <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
        <div>
          <h6 className="fw-bold mb-0">Academic Programs</h6>
          <small className="text-muted">Programs define the container for course projects.</small>
        </div>
        <Button variant="primary" size="sm" onClick={() => setShowCreate(true)}>+ Create Program</Button>
      </Card.Header>
      <Card.Body className="p-0">
        {error && <Alert variant="danger" className="m-3" role="alert">{error}</Alert>}
        <Table hover responsive className="mb-0 align-middle">
          <thead className="table-light small text-uppercase">
            <tr>
              <th scope="col" className="ps-4">Code</th>
              <th>Name</th>
              <th>Credits</th>
              <th>Status</th>
              <th scope="col" className="text-end pe-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedPrograms.map((p) => (
              <tr key={p.id}>
                <td className="ps-4"><Badge bg="light" text="dark" className="border">{p.code}</Badge></td>
                <td className="fw-medium">{p.name}</td>
                <td>{p.credits || '-'}</td>
                <td>
                    <Badge bg={p.status === 'active' ? 'success' : 'secondary'}>{p.status || 'active'}</Badge>
                </td>
                <td className="text-end pe-4">
                  <Button 
                    variant="link" size="sm" className="text-decoration-none"
                    onClick={() => toggleStatus(p)} disabled={updatingId === p.id}
                  >
                    {updatingId === p.id ? <Spinner size="sm" aria-hidden="true" /> : p.status === 'inactive' ? 'Activate' : 'Deactivate'}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>

      <Modal show={showCreate} onHide={() => setShowCreate(false)} centered>
        <Modal.Header closeButton className="border-0"><Modal.Title className="fw-bold">New Program</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3"><Form.Label className="small fw-bold">Code</Form.Label><Form.Control placeholder="e.g. BSC-CS" value={form.code} onChange={e => setForm({...form, code: e.target.value})} /></Form.Group>
          <Form.Group className="mb-3"><Form.Label className="small fw-bold">Name</Form.Label><Form.Control placeholder="e.g. Bachelor of Computer Science" value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></Form.Group>
          <Form.Group className="mb-3"><Form.Label className="small fw-bold">Credits</Form.Label><Form.Control type="number" value={form.credits} onChange={e => setForm({...form, credits: e.target.value})} /></Form.Group>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="light" onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleCreate} disabled={creating}>{creating ? <Spinner size="sm" aria-hidden="true" /> : 'Save Program'}</Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default ProgramTable;
